import { useState } from "react";
import { Play } from "lucide-react";

/**
 * YouTubeFacade — lightweight "click-to-play" YouTube embed.
 *
 * A stock `<iframe src="youtube.com/embed/…">` costs ~1-2s of LCP and
 * pulls in 500KB+ of YouTube JavaScript that most landing-page visitors
 * never actually watch. The classic "facade" pattern (used by Slack,
 * Airtable, Vercel, etc.) shows the video's own thumbnail with a play
 * button overlay and only mounts the real iframe after the parent taps
 * play. LCP drops ~1.5-2s on the landing page as a result.
 *
 * Props:
 *   videoId : string   — YouTube video ID (11 chars). Required.
 *   title   : string   — Accessible title used for the play button label
 *                        and passed through to the mounted iframe.
 *   testId? : string   — data-testid override for the iframe / facade
 *                        wrapper so existing Playwright tests keep
 *                        finding the element.
 *   className? : string — Extra classes for the outer wrapper (defaults
 *                        to a 16:9 aspect-video container).
 *   startMuted? : boolean — Force `mute=1` when the iframe mounts. Useful
 *                        for autoplay-adjacent contexts (banner video)
 *                        where sound would be jarring. Default: true.
 *
 * Full-HD lock: the mounted iframe includes `vq=hd1080&hd=1&enablejsapi=1`
 * URL hints matching the rest of the landing embeds. Real quality is
 * still subject to YouTube's adaptive-bitrate algorithm on very slow
 * connections, but 1080p is requested up-front.
 */
export default function YouTubeFacade({
  videoId,
  title,
  testId = "youtube-facade",
  className = "relative rounded-3xl overflow-hidden shadow-soft-md border border-plum/10 aspect-video bg-black",
  startMuted = true,
}) {
  const [play, setPlay] = useState(false);
  // maxresdefault is the highest-quality auto-generated thumbnail
  // YouTube exposes. Some very old videos don't have it — for those the
  // <img>'s onError bumps down to hqdefault (guaranteed to exist).
  const [thumb, setThumb] = useState(
    `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`
  );
  const handleThumbError = () => {
    // Only fall back once so we don't loop on a broken hqdefault too.
    setThumb((current) =>
      current.includes("hqdefault") ? current : `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    );
  };

  if (play) {
    return (
      <div className={className}>
        <iframe
          data-testid={testId}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&${startMuted ? "mute=1&" : ""}rel=0&modestbranding=1&playsinline=1&vq=hd1080&hd=1&enablejsapi=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlay(true)}
      aria-label={`Play video: ${title}`}
      data-testid={`${testId}-facade`}
      // `block w-full` is load-bearing: without it the <button>'s default
      // display:inline-block collapses ``aspect-video`` to 0×0 because
      // Tailwind's ``aspect-*`` uses ``aspect-ratio`` which needs a
      // resolvable width. A plain <div>+role="button" would sidestep
      // this but we prefer the native button for a11y (Enter/Space,
      // focus ring, screen-reader announcement).
      className={`${className} group cursor-pointer p-0 border-0 block w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/60`}
    >
      {/* Thumbnail — lazy-loaded so it never blocks LCP. Sized-full so
          the browser can pre-lay-out the aspect ratio. */}
      <img
        src={thumb}
        onError={handleThumbError}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Subtle darken so the play button stays legible on bright frames. */}
      <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-black/0 group-hover:from-black/55 transition-colors" aria-hidden="true" />
      {/* YouTube-style play button — red pill with white triangle. Scales
          on hover / focus so keyboard users see a clear affordance. */}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 shadow-2xl group-hover:scale-110 group-focus-visible:scale-110 transition-transform">
          <Play className="w-7 h-7 sm:w-9 sm:h-9 text-coral fill-coral ml-1" strokeWidth={0} />
        </span>
      </span>
    </button>
  );
}
